/**
 * A continuous time range from `start` to `end`.
 */
class ContinuousTimeRange {
  constructor(readonly start: Date, readonly end: Date) {}

  /**
   * Return the length of the time range in milliseconds
   */
  get length(): number {
    return Math.abs(this.end.getTime() - this.start.getTime())
  }
}

/**
 * Check if `superset` (S) is a superset of `subset` (O): S ⊇ O.
 */
function is_superset(
  superset: ContinuousTimeRange,
  subset: ContinuousTimeRange
): boolean {
  return superset.start <= subset.start && subset.end >= superset.end
}

/**
 * Check if `set` (S) contains `moment` (M): M ∈ S.
 */
function contains(set: ContinuousTimeRange, moment: Date): boolean {
  return set.start <= moment && moment <= set.end
}

/**
 * Return the intersection of `a` and `b` if exists: A ∩ B.
 */
function intersection_c(
  a: ContinuousTimeRange,
  b: ContinuousTimeRange
): ContinuousTimeRange | null {
  if (!intersects(a, b)) {
    return null
  }
  return new ContinuousTimeRange(
    max_date(a.start, b.start),
    min_date(a.end, b.end)
  )
}

/**
 * Check if `a` has an intersection with `b`: A ∩ B.
 */
function intersects(a: ContinuousTimeRange, b: ContinuousTimeRange): boolean {
  return contains(b, a.start) || contains(b, a.end) || is_superset(a, b)
}

/**
 * Returns the union of the given interval.
 *
 * FIXME: Currently it is assumed that the resulting interval is continuous, but it should be checked.
 */
function union_c(...intervals: ContinuousTimeRange[]): ContinuousTimeRange {
  return new ContinuousTimeRange(
    min_date(...intervals.map(i => i.start)),
    max_date(...intervals.map(i => i.end))
  )
}

/**
 * A time range which can consist of multiple discontinuous parts.
 */
export class TimeRange {
  /**
   *  A set of not intersecting intervals that constitute the time range
   *  in chronological order
   */
  intervals: ContinuousTimeRange[] = []

  constructor(start: Date, end: Date) {
    this.intervals.push(new ContinuousTimeRange(start, end))
  }

  /**
   * Return the length of the time range in milliseconds
   */
  get length(): number {
    return sum(this.intervals.map(i => i.length))
  }
}

/**
 * Returns the union of the given time ranges.
 */
export function union(...timeranges: TimeRange[]): TimeRange {
  const rawIntervals: ContinuousTimeRange[] = timeranges
    .map(timerange => timerange.intervals)
    .flat()
  // Not intersecting continuous intervals that constitute the union of the given time ranges
  let intervals: ContinuousTimeRange[] = []
  for (const newInterval of rawIntervals) {
    const intersectingIntervals: ContinuousTimeRange[] = [newInterval]
    const notIntersectingIntervals: ContinuousTimeRange[] = []
    for (const interval of intervals) {
      if (intersects(newInterval, interval)) {
        intersectingIntervals.push(interval)
      } else {
        notIntersectingIntervals.push(interval)
      }
    }
    intervals = notIntersectingIntervals
    intervals.push(union_c(...intersectingIntervals))
  }
  const timerange = new TimeRange(new Date(), new Date())
  timerange.intervals = intervals
  return timerange
}

export function intersection(a: TimeRange, b: TimeRange): TimeRange {
  let intervals = [...a.intervals] // the spread operator is used for copying the array
  for (const bi of b.intervals) {
    const newIntervals: ContinuousTimeRange[] = []
    for (const ai of intervals) {
      const intsec = intersection_c(ai, bi)
      if (intsec != null) {
        newIntervals.push(intsec)
      }
    }
    intervals = newIntervals
  }
  const timerange = new TimeRange(new Date(), new Date())
  timerange.intervals = intervals
  return timerange
}

/**
 * Return the smalls of the given dates.
 * @remarks
 * See https://stackoverflow.com/a/7143601/9035706
 *
 * TODO What to return if no dates are given?
 */
function min_date(...dates: Date[]): Date {
  return new Date(Math.min.apply(null, dates))
}

/**
 * Return the biggest of the given dates.
 * @remarks
 * See https://stackoverflow.com/a/7143601/9035706
 *
 * TODO What to return if no dates are given?
 */
function max_date(...dates: Date[]): Date {
  return new Date(Math.max.apply(null, dates))
}

export function sum(arr: any[]): number {
  return arr.reduce((accumulator, current) => {
    return accumulator + current
  }, 0)
}
